import type { Message } from '@/types';
import { cn, getChatMessageWithImagesWidthClasses, getChatMessageTextOnlyWidthClasses, getImageSizeClassesHalfWidth, getImageSizeClassesFullWidth, getImageGridClasses } from '@/utils';
import { ChatImage } from './ChatImage';
import { ImagePlaceholder } from '../ImagePlaceholder';
import { useImageState } from '@/contexts/ImageContext';

interface ChatMessageProps {
  readonly message: Message;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const { isHalfWidth } = useImageState();
  const hasImages = Boolean(message.imageUrls && message.imageUrls.length > 0);

  return (
    <div className={cn(
      'flex w-full',
      isUser ? 'justify-end' : 'justify-start'
    )}>
      <div className={cn(
        // Dynamic width based on content using utility
        hasImages ? getChatMessageWithImagesWidthClasses() : getChatMessageTextOnlyWidthClasses(),
        'shadow-sm border',
        'p-4',
        // User message styling (right side)
        isUser && [
          'bg-slate-300 dark:bg-slate-600 text-slate-800 dark:text-white',
          'border-slate-400 dark:border-slate-700',
          'ml-auto',
          'rounded-xl rounded-br-none' // Sharp bottom-right corner for user, more rounded other corners
        ],
        // Assistant message styling (left side)
        !isUser && [
          'bg-slate-100 dark:bg-gray-800',
          'border-slate-200 dark:border-gray-700',
          'text-slate-700 dark:text-gray-100',
          'rounded-xl rounded-bl-none' // Sharp bottom-left corner for assistant, more rounded other corners
        ]
      )}>
        <div className="flex flex-col">
          {/* Message text content */}
          {message.content && (
            <div className={cn(
              'text-sm leading-relaxed whitespace-pre-wrap break-words',
              isUser 
                ? 'text-slate-800 dark:text-white' 
                : 'text-slate-700 dark:text-gray-100'
            )}>
              {message.content}
            </div>
          )}

          {/* Display image placeholders if assistant message expects images but none loaded yet */}
          {message.role === 'assistant' && message.expectedImageCount && (!message.imageUrls || message.imageUrls.length === 0) && (
            <div className="mt-3">
              <div className="text-xs opacity-70 mb-2 text-gray-500 dark:text-gray-400">Generating Images...</div>
              <div className={getImageGridClasses()}>
                {Array.from({ length: message.expectedImageCount }).map((_, idx) => (
                  <div 
                    key={`placeholder-${message.id}-${idx}`} 
                    className={isHalfWidth ? getImageSizeClassesHalfWidth() : getImageSizeClassesFullWidth()}
                  >
                    <ImagePlaceholder className="w-full h-full" />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Display images if available */}
          {message.imageUrls && message.imageUrls.length > 0 && (
            <div className={message.content ? "mt-3" : ""}>
              <div className={cn(
                getImageGridClasses(),
                // Add user chat bubble background for user images
                isUser && "bg-slate-300 dark:bg-slate-600 p-2 rounded-lg"
              )}>
                {message.imageUrls.map((imageUrl, index) => (
                  <div 
                    key={`image-${message.id}-${index}-${imageUrl.substring(imageUrl.lastIndexOf('/') + 1, imageUrl.lastIndexOf('/') + 20)}`} 
                    className={isHalfWidth ? getImageSizeClassesHalfWidth() : getImageSizeClassesFullWidth()}
                  >
                    <ChatImage
                      src={imageUrl}
                      alt={`${message.role === 'user' ? 'Uploaded' : 'Generated'} image ${index + 1}`}
                      index={index}
                      messageId={message.id}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
